import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  AlertCircle, MapPin, Clock, Truck, Utensils,
  Image as ImageIcon, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { getTodayHours } from '@/features/storefront/utils/storeStatus';
import { formatAddress, formatAddressShort } from '@/features/storefront/utils/addressFormat';
import { getEstimatedTimeForNow, formatEstimatedTimeRange } from '@/features/storefront/utils/estimatedTime';
import type { RestaurantResponse } from '@/features/restaurants/types';

interface CarouselItem {
  id: string;
  title: string;
  subtitle: string;
  url: string;
}

interface LandingHeroSectionProps {
  restaurant: RestaurantResponse;
  restaurantId: string;
  carouselItems: CarouselItem[];
  currentSlide: number;
  setCurrentSlide: (i: number) => void;
  prevSlide: () => void;
  nextSlide: () => void;
  onDeliveryCheckOpen: () => void;
}

function formatCurrency(amount: number | undefined, currency: string): string {
  if (amount == null) return 'N/A';
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
}

export function LandingHeroSection({
  restaurant,
  restaurantId,
  carouselItems,
  currentSlide,
  setCurrentSlide,
  prevSlide,
  nextSlide,
  onDeliveryCheckOpen,
}: LandingHeroSectionProps) {
  const { t } = useTranslation();
  const currency = restaurant.currency || 'EUR';

  const isOpen = restaurant.isStoreOpen &&
    ((restaurant.hasDelivery && restaurant.isDeliveryOpen) || restaurant.hasPickup);
  const isTempClosed = !!(restaurant.isTemporarilyClosed || restaurant.closedReason || restaurant.closeMessage);

  const deliveryEst = restaurant.hasDelivery && restaurant.isDeliveryOpen
    ? getEstimatedTimeForNow(restaurant.deliveryTimeRanges)
    : null;

  const deliveryFeeText = (() => {
    if (!restaurant.hasDelivery || !restaurant.isDeliveryOpen) return null;
    if (restaurant.deliveryZones?.length) {
      const fees = restaurant.deliveryZones.map(z => z.deliveryFee);
      const min = Math.min(...fees);
      return min === 0 ? t('landing.freeDelivery') : `${t('landing.from')} ${formatCurrency(min, currency)}`;
    }
    if (restaurant.deliveryFee !== undefined) {
      return restaurant.deliveryFee === 0
        ? t('landing.freeDelivery')
        : formatCurrency(restaurant.deliveryFee, currency);
    }
    return null;
  })();

  const todayHours = getTodayHours(restaurant.openingHours);

  return (
    <section className="relative overflow-hidden">
      {/* Atmospheric background — simplified to reduce paint cost */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-orange-100/25 blur-2xl" />
        <div className="absolute -bottom-32 -left-32 w-[380px] h-[380px] rounded-full bg-amber-50/50 blur-2xl" />
      </div>

      <div className="relative max-w-6xl mx-auto px-3 sm:px-4 pt-10 pb-16 sm:py-16 md:py-20 lg:py-24">
        <div className="grid lg:grid-cols-[1fr_1.08fr] gap-10 lg:gap-14 xl:gap-20 items-center">

          {/* ── Left: Info ───────────────────────────────────────────── */}
          <div className="order-2 lg:order-1 flex flex-col animate-in fade-in slide-in-from-left-6 duration-700">

            {restaurant.subHeading && (
              <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.18em] text-muted-foreground mb-3 sm:mb-4 animate-in fade-in slide-in-from-top-2 duration-500 delay-100">
                {restaurant.subHeading}
              </p>
            )}

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.04] mb-4 sm:mb-5 text-foreground animate-in fade-in slide-in-from-bottom-4 duration-700 delay-150">
              {restaurant.name}
            </h1>

            <div className="mb-5 sm:mb-6 animate-in fade-in zoom-in-95 duration-500 delay-[250ms]">
              {isTempClosed ? (
                <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold bg-warning/10 text-warning border border-warning/25">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                  {t('landing.temporarilyClosed')}
                  {(restaurant.closedReason || restaurant.closeMessage) && ` — ${restaurant.closedReason || restaurant.closeMessage}`}
                </span>
              ) : isOpen ? (
                <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold bg-success/10 text-success border border-success/25">
                  <span className="w-2 h-2 rounded-full bg-success shrink-0 animate-pulse" />
                  {restaurant.hasDelivery && restaurant.isDeliveryOpen && restaurant.hasPickup
                    ? t('landing.acceptingDeliveryPickup')
                    : restaurant.hasDelivery && restaurant.isDeliveryOpen
                      ? t('landing.acceptingDelivery')
                      : restaurant.hasDelivery && !restaurant.isDeliveryOpen && restaurant.hasPickup
                        ? t('landing.acceptingPickupDeliveryClosed')
                        : t('landing.acceptingPickup')}
                </span>
              ) : (
                <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold bg-destructive/10 text-destructive border border-destructive/25">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                  {t('landing.currentlyClosed')}
                </span>
              )}
            </div>

            {restaurant.description && (
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-5 max-w-lg line-clamp-3 sm:line-clamp-none animate-in fade-in slide-in-from-bottom-2 duration-500 delay-300">
                {restaurant.description}
              </p>
            )}

            {(restaurant.street || restaurant.city) && (
              <a
                href={
                  restaurant.latitude != null && restaurant.longitude != null
                    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([restaurant.name, `${restaurant.latitude},${restaurant.longitude}`].filter(Boolean).join(', '))}`
                    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([restaurant.name, formatAddress(restaurant)].filter(Boolean).join(', '))}`
                }
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-5 transition-colors group min-w-0 max-w-full animate-in fade-in duration-500 delay-[350ms]"
                aria-label={t('landing.openInGoogleMaps', 'Open address in Google Maps')}
              >
                <MapPin className="w-4 h-4 shrink-0 group-hover:text-accent transition-colors" aria-hidden />
                <span className="truncate sm:hidden">{formatAddressShort(restaurant)}</span>
                <span className="truncate hidden sm:inline">{formatAddress(restaurant)}</span>
              </a>
            )}

            <div className="flex flex-wrap gap-2 mb-7 sm:mb-8 animate-in fade-in slide-in-from-bottom-2 duration-500 delay-[400ms]">
              {restaurant.isStoreOpen && todayHours && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-muted border border-border text-muted-foreground">
                  <Clock className="w-3.5 h-3.5 shrink-0" />
                  {todayHours}
                </span>
              )}
              {restaurant.hasDelivery && restaurant.isDeliveryOpen && deliveryFeeText && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-muted border border-border text-muted-foreground">
                  <Truck className="w-3.5 h-3.5 shrink-0" />
                  {deliveryFeeText}
                </span>
              )}
              {restaurant.hasDelivery && !restaurant.isDeliveryOpen && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-warning/8 border border-warning/20 text-warning">
                  <Truck className="w-3.5 h-3.5 shrink-0" />
                  {t('landing.deliveryClosed')}
                </span>
              )}
              {restaurant.hasPickup && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-muted border border-border text-muted-foreground">
                  <Utensils className="w-3.5 h-3.5 shrink-0" />
                  {t('landing.pickupAvailable')}
                </span>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 animate-in fade-in slide-in-from-bottom-3 duration-500 delay-500">
              <Button
                size="lg"
                className="bg-foreground text-background hover:bg-foreground/88 gap-2.5 shadow-lg shadow-foreground/10 h-12 sm:h-13 px-7 sm:px-8 text-sm sm:text-base font-semibold w-full sm:w-auto hover:scale-[1.02] active:scale-[0.97] transition-transform"
                asChild
              >
                <Link to={`/${restaurantId}/menu`} aria-label={t('landing.orderFood')}>
                  <Utensils className="w-4 h-4 sm:w-[18px] sm:h-[18px]" />
                  {t('landing.orderOnline')}
                </Link>
              </Button>
              {restaurant.hasDelivery && (
                <Button
                  size="lg"
                  variant="outline"
                  className="gap-2.5 h-12 sm:h-13 px-7 sm:px-8 text-sm sm:text-base font-semibold w-full sm:w-auto border-border hover:bg-muted/60 hover:scale-[1.02] active:scale-[0.97] transition-transform"
                  onClick={onDeliveryCheckOpen}
                  aria-label={t('landing.checkDelivery', 'Check delivery')}
                >
                  <Truck className="w-4 h-4 sm:w-[18px] sm:h-[18px]" />
                  {t('landing.checkDelivery', 'Check delivery')}
                </Button>
              )}
            </div>
          </div>

          {/* ── Right: Image ─────────────────────────────────────────── */}
          <div className="order-1 lg:order-2 relative animate-in fade-in zoom-in-95 duration-700 delay-[250ms]">
            {/* Outer decorative ring */}
            <div className="absolute -inset-3 sm:-inset-4 lg:-inset-5 rounded-[2.8rem] border-2 border-dashed border-border/20" aria-hidden="true" />

            {/* Image container — CSS crossfade, no AnimatePresence */}
            <div className="relative rounded-[2.2rem] overflow-hidden aspect-[4/3] shadow-2xl shadow-foreground/8 border border-border/25">
              {carouselItems.length > 0
                ? carouselItems.map((item, i) => (
                    <div
                      key={item.id}
                      className="absolute inset-0 transition-opacity duration-500 ease-in-out"
                      style={{ opacity: i === currentSlide ? 1 : 0 }}
                    >
                      {item.url && item.url !== '/placeholder.svg' ? (
                        <>
                          <img
                            src={item.url}
                            alt={item.title}
                            className="absolute inset-0 w-full h-full object-cover"
                            loading={i === 0 ? 'eager' : 'lazy'}
                            decoding="async"
                            width={800}
                            height={600}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent" />
                        </>
                      ) : (
                        <div className="absolute inset-0 bg-gradient-to-br from-orange-50 via-amber-50/60 to-white flex flex-col items-center justify-center gap-4 p-8">
                          <div className="w-20 h-20 rounded-2xl bg-muted border border-border flex items-center justify-center">
                            <ImageIcon className="w-10 h-10 text-muted-foreground/40" />
                          </div>
                          <div className="text-center">
                            <h3 className="text-2xl font-bold text-foreground">{restaurant.name}</h3>
                            {restaurant.subHeading && (
                              <p className="text-sm text-muted-foreground mt-1">{restaurant.subHeading}</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                : (
                  <div className="absolute inset-0 bg-gradient-to-br from-orange-50 via-amber-50/60 to-white flex flex-col items-center justify-center gap-4 p-8">
                    <div className="w-20 h-20 rounded-2xl bg-muted border border-border flex items-center justify-center">
                      <ImageIcon className="w-10 h-10 text-muted-foreground/40" />
                    </div>
                    <div className="text-center">
                      <h3 className="text-2xl font-bold text-foreground">{restaurant.name}</h3>
                      {restaurant.subHeading && (
                        <p className="text-sm text-muted-foreground mt-1">{restaurant.subHeading}</p>
                      )}
                    </div>
                  </div>
                )}

              {/* Carousel controls */}
              {carouselItems.length > 1 && (
                <>
                  <button
                    onClick={prevSlide}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-white/80 dark:bg-card/80 backdrop-blur-md border border-white/60 dark:border-border/60 flex items-center justify-center hover:bg-white dark:hover:bg-card transition-all shadow-lg hover:scale-110 text-foreground touch-manipulation"
                    aria-label={t('landing.previousImage')}
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={nextSlide}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-white/80 dark:bg-card/80 backdrop-blur-md border border-white/60 dark:border-border/60 flex items-center justify-center hover:bg-white dark:hover:bg-card transition-all shadow-lg hover:scale-110 text-foreground touch-manipulation"
                    aria-label={t('landing.nextImage')}
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>

                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                    {carouselItems.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setCurrentSlide(i)}
                        className={`h-1.5 rounded-full transition-all duration-300 touch-manipulation ${
                          i === currentSlide
                            ? 'bg-white w-6'
                            : 'bg-white/50 w-1.5 hover:bg-white/70'
                        }`}
                        aria-label={t('landing.goToSlide', { number: i + 1 })}
                        aria-current={i === currentSlide ? 'true' : undefined}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Floating open/hours card */}
            <div className="absolute -top-4 sm:-top-5 right-6 sm:right-10 bg-card/90 dark:bg-card backdrop-blur-md border border-border/70 rounded-2xl px-3.5 py-2.5 shadow-xl animate-in fade-in slide-in-from-top-3 zoom-in-95 duration-500 delay-700">
              <div className="flex items-center gap-2">
                {isOpen ? (
                  <>
                    <span className="w-2 h-2 rounded-full bg-success shrink-0 animate-pulse" />
                    <span className="text-xs font-semibold text-success">{t('landing.navOpen')}</span>
                  </>
                ) : (
                  <>
                    <span className="w-2 h-2 rounded-full bg-destructive shrink-0" />
                    <span className="text-xs font-semibold text-destructive">{t('app.closed')}</span>
                  </>
                )}
              </div>
              {todayHours && (
                <p className="text-[10px] text-muted-foreground mt-0.5 font-medium">{todayHours}</p>
              )}
            </div>

            {/* Floating delivery time card */}
            {deliveryEst && (
              <div className="absolute -bottom-5 sm:-bottom-6 left-6 sm:left-10 bg-card/90 dark:bg-card backdrop-blur-md border border-border/70 rounded-2xl pl-3 pr-4 py-2.5 shadow-xl flex items-center gap-2.5 animate-in fade-in slide-in-from-bottom-3 zoom-in-95 duration-500 delay-[850ms]">
                <div className="w-8 h-8 rounded-xl bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center shrink-0">
                  <Truck className="w-4 h-4 text-blue-500" />
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wide leading-none">
                    {t('landing.deliveryTimeEstimate', 'Lieferzeit')}
                  </p>
                  <p className="text-sm font-bold text-foreground leading-tight mt-0.5">
                    {formatEstimatedTimeRange(deliveryEst.minMinutes, deliveryEst.maxMinutes)}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Wave divider */}
      <div className="absolute bottom-0 left-0 right-0 pointer-events-none" aria-hidden="true">
        <svg viewBox="0 0 1440 48" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path d="M0 48V24C240 40 480 48 720 40C960 32 1200 16 1440 24V48H0Z" fill="hsl(var(--background))" />
        </svg>
      </div>
    </section>
  );
}
